import React, { useEffect, useRef, useState } from 'react'
import {
  hiddenSceneStates,
  ignoreNextStates,
  states,
} from '../../../constants/game-platform-settings'
import { infinityLevel } from '../../../constants/game-platform-levels'
import classNames from 'classnames'
import useStateReducer from '../../../utils/scene/react/hooks/useStateReducer'
import { getNextState } from '../../../utils/scene/utils/state/state'
import CustomButton from '../../features/customButton/ui/main/CustomButton'
import Counter from '../../baseComponents/gui/counter/Counter'
import { CSSTransition } from 'react-transition-group'
import { useContent } from '../../../redux/reducer/content'
import { useGameRules } from '../../../hooks/useGameRules'
import { useRequests } from '../../../redux/reducer/requests'
import { useGamesModal } from '../../../hooks/useGamesModal'
import AppLoader from '../../features/appLoader/AppLoader'
import { Stats } from '../../features/stats'
import useGameLog from '../../../hooks/useGameLog'
import { useGameControls } from '../../../hooks/useGameControls'
import { image } from '../../../utils/data/baseUrl'
import { useBoostersTutorial } from '../../../hooks/useBoostersTutorial'
import { GameWrapper } from '../../features/gameWrapper/GameWrapper'
import { eventSubscription } from '../../../utils/events/eventSubscription'
import { isAlfaGameActive } from '../../../utils/game/isAlfaGameActive'
import { usePreloadedGameWrapper } from '../../../hooks/usePreloadedGameWrapper'
import { isPlatformBourjoisAssets } from '../../../utils/game/isPlatformBourjoisAssets'

const defaultGameData = {
  lifes: 1,
  points: 0,
  coins: 0,
  targetCoins: 50,
  levelData: infinityLevel,
}

const Platformer = React.forwardRef(function Platformer({ callbacks }, ref) {
  const { gameDifficulty } = useContent()
  const {
    authData: { isWeb },
  } = useRequests()
  const preloadGameWrapper = usePreloadedGameWrapper('platform') // предзагруженный wrapper

  const { rulesModalCallback } = useGameRules()

  // Контейнер куда будет монтироваться сanvas
  const containerRef = useRef()
  const [state, setState] = useState('loadingManifest')
  const [wrapper, setWrapper] = useState(null)
  const [gameData, setGameData] = useState(defaultGameData)
  // Таймер обратного отсчёта перед началом уровня
  const [gameTimer, setGameTimer] = useState(3)

  // Логирование событий игры
  const logs = useGameLog(wrapper?.eventBus)

  // Основная логика управления игрой (обработка победы, поражения, подсчёт очков)
  const { win } = useGameControls({
    wrapper,
    isInfinity: true,
    gameType: 'runner',
    logs,
    gameData,
    callbacks,
  })

  // Управление модальными окнами (пауза, ожидание загрузки, обработка проигрыша)
  const { openPauseModal, isPending } = useGamesModal({
    game: 'runner',
    loseCallback: win,
    setGameState: setState,
    callbacks,
  })

  // Обработка логики при поражении
  const gameOver = () => {
    win()
    setGameData(defaultGameData)
  }

  // Указываем что делаем при переходе в состояние игры (при паузе показываем модальное окно и тп)
  useStateReducer(
    {
      showing() {
        rulesModalCallback(() => setState(getNextState(states, state)))
      },
      paused: openPauseModal,
      lose: gameOver,
      win: gameOver,
    },
    ignoreNextStates,
    (state) => setState(getNextState(states, state)),
    state,
    wrapper,
  )

  // Хук для отображения модального окна при захвате бустера с пояснением его свойств
  useBoostersTutorial({
    wrapper,
    toTutorial: () => {
      setState('boosterTutorial')
    },
    toPlaying: () => {
      setGameTimer(3)
      setState('countdown')
    },
    type: 'platform',
  })

  // Подставляем предзагруженный wrapper из стора в локальный компонент
  useEffect(() => {
    if (preloadGameWrapper && !wrapper) {
      setWrapper(preloadGameWrapper)
    }
  }, [preloadGameWrapper]) // Срабатывает, когда предзагруженный wrapper становится доступен

  // Инициализация wrapper после его загрузки (настройка сложности и цели монет)
  useEffect(() => {
    if (!wrapper || !containerRef.current) {
      return
    }

    wrapper.appendContainer(containerRef.current)
    wrapper.setDifficulty(gameDifficulty)
    wrapper.setTargetCoins(gameData.targetCoins)
    wrapper.controller.levelData = gameData.levelData
  }, [wrapper, gameDifficulty])

  // Подписка на события игрового движка (потеря жизни, изменение счёта, монет и тд)
  useEffect(() => {
    if (!wrapper) {
      return
    }

    const { eventBus } = wrapper

    return eventSubscription({
      target: eventBus,
      callbacksBus: [
        {
          event: 'game-over',
          callback({ data: { isWin } }) {
            setState(isWin ? 'win' : 'lose')
          },
        },
        {
          event: 'gameData:update',
          callback({ points, coins }) {
            const updatedData = [
              { key: 'points', value: points },
              { key: 'coins', value: coins },
            ].reduce((acc, field) => {
              return typeof field.value === 'number' ? { ...acc, [field.key]: field.value } : acc
            }, {})

            setGameData((prev) => ({ ...prev, ...updatedData }))
          },
        },
      ],
    })
  }, [wrapper])

  // Очистка при размонтировании (сбрасываем игру)
  useEffect(
    () => () => {
      if (!wrapper) {
        return
      }

      wrapper.reset(true)
    },
    [wrapper],
  )

  return (
    <GameWrapper>
      <div
        className={classNames('game', {
          alfa: isAlfaGameActive(),
          bourjois: isPlatformBourjoisAssets(),
        })}
      >
        <div className={'game__content game__content_platformer'}>
          <CSSTransition
            in={!hiddenSceneStates.includes(state)}
            classNames={'game__wrapper'}
            appear={true}
          >
            <div
              className={'game__wrapper'}
              ref={containerRef}
            />
          </CSSTransition>

          <div className={`game__header ${isWeb ? '' : 'app'}`}>
            <CustomButton
              className={'game__pause button_pause'}
              icon={'buttons/pause'}
              onClick={() => state === 'playing' && setState('paused')}
            />
            <Stats
              eventBus={wrapper?.eventBus}
              score={gameData.points}
              count={gameData.coins}
              coins={gameData.coins}
              targetCoins={gameData.targetCoins}
              timer={{ count: 30, img: image('games/time/1.png') }}
              type={'platformer'}
            />
          </div>
          <Counter
            gameTimer={gameTimer}
            setGameTimer={setGameTimer}
            state={state}
            setState={setState}
            mod={'platform'}
          />
        </div>

        {isPending && <AppLoader />}
      </div>
    </GameWrapper>
  )
})

export default Platformer
